import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import GoogleMapReact from 'google-map-react';
import { useTheme } from 'emotion-theming';
import { Global } from '@emotion/core';
import { GOOGLE_MAPS_API_KEY } from 'config';
import LoadingIndicator from 'components/maps/LoadingIndicator';
import PageError from 'components/errors/PageError';
import { hasBounds, DEFAULT_ZOOM, positionMapToCenter, fitBounds } from './mapUtils';
import mapStyles from './mapStyles';
import { getQueryNeLat, getQuerySwLat, getQueryNeLng, getQuerySwLng } from 'store/search/searchSelectors/querySelectors';
import { captureErrorInSentry } from 'lib/errors';

const GoogleMap = ({ center, onMapAPILoaded, onMapTilesLoaded, onClick, onMapMoved, error, options, ready, children }) => {
  const [map, setMap] = useState({});
  const theme = useTheme();
  const neLat = useSelector(getQueryNeLat);
  const swLat = useSelector(getQuerySwLat);
  const neLng = useSelector(getQueryNeLng);
  const swLng = useSelector(getQuerySwLng);

  const recenterMap = useCallback(
    (gMap) => {
      if (hasBounds({ neLat, swLat, neLng, swLng })) {
        fitBounds(gMap, {
          north: Number(neLat),
          south: Number(swLat),
          east: Number(neLng),
          west: Number(swLng),
        });
      } else {
        positionMapToCenter(gMap, center);
      }
    },
    [neLat, swLat, neLng, swLng, center],
  );

  const onGoogleApiLoaded = useCallback(
    ({ map, maps }) => {
      // When (if) the google maps API has loaded, center the map to the search results
      if (map && maps) {
        recenterMap(map);
        setMap({ gMap: map, gMaps: maps });
        onMapAPILoaded({ gMap: map, gMaps: maps });
      } else {
        captureErrorInSentry(new Error('Google Map has failed to load'));
      }
    },
    [recenterMap, setMap, onMapAPILoaded],
  );

  const onTilesLoaded = useCallback(() => {
    // When the map assets have actually loaded, then update the UI
    if (!ready && onMapTilesLoaded) {
      onMapTilesLoaded(true);
    }
  }, [ready, onMapTilesLoaded]);

  if (isEmpty(center)) {
    return <LoadingIndicator />;
  }

  return (
    <Fragment>
      <Global styles={mapStyles} />
      <GoogleMapReact
        data-testid="google-map-react"
        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
        center={center}
        defaultZoom={DEFAULT_ZOOM}
        options={{
          ...options,
          backgroundColor: theme.colors.greys.porcelain,
        }}
        onClick={onClick}
        onDragEnd={onMapMoved}
        onZoomAnimationStart={onMapMoved}
        onTilesLoaded={onTilesLoaded}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={onGoogleApiLoaded}
      >
        {!isEmpty(map) && children}
      </GoogleMapReact>
      {(((map.gMap && map.gMap.error) || error) && <PageError />) || (isEmpty(map) && <LoadingIndicator />)}
    </Fragment>
  );
};

GoogleMap.propTypes = {
  center: PropTypes.shape({
    latitude: PropTypes.number,
    longitude: PropTypes.number,
  }),
  onMapAPILoaded: PropTypes.func,
  onMapTilesLoaded: PropTypes.func,
  onClick: PropTypes.func,
  onMapMoved: PropTypes.func,
  error: PropTypes.bool,
  options: PropTypes.object,
  ready: PropTypes.bool,
  children: PropTypes.node,
};

GoogleMap.defaultProps = {
  center: undefined,
  onMapAPILoaded: () => {},
  onMapTilesLoaded: () => {},
  onClick: () => {},
  onMapMoved: () => {},
  error: false,
  options: {},
  ready: false,
  children: null,
};

export default GoogleMap;
